import { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Form, message, Table, Modal, Switch, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';
import SettingsMenu from '../components/settings/SettingsMenu';
import { useAppSelector } from '../store/hooks';
import Button from '../UI/buttons/Button';
import FormItem from '../components/form/FormItem';
import { RecruiterProfile } from '../types';
import RadioButtonTab from '../UI/buttons/RadioButtonTab'; // Keep if needed for Invite Form
import {
    createRecruiterProfile,
    getCompanyRecruiters,
    toggleRecruiterActiveStatus
} from '../api/recruiter';
import Input from '../UI/inputs/Input';
import NameImage from '../components/client/NameImage';
import useHirePortTheme from '../hooks/useHirePortTheme';
import {
    UserAddOutlined,
    TeamOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import GooglePlacesInput from '../UI/inputs/GooglePlacesInput';

// Re-use styled components from original file
const PageWrapper = styled.section`
  background: #f9fafb;
  width: 100%;
  display: flex;
  min-height: 100vh;
  flex-flow: row-reverse;
`;

const FormWrapper = styled.div`
  background: #fff;
  width: 61%;
  padding: 4.5rem 4rem;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  margin: 24px;

  @media (max-width: 1200px) {
    width: 70%;
  }
  @media (max-width: 768px) {
    width: 90%;
    padding: 3rem;
    margin: 16px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const StyledForm = styled(Form)`
  margin-bottom: 3rem;
`;

const StyledTable = styled(Table)`
  margin-top: 1rem;
  margin-bottom: 2rem;

  & .ant-table {
    border-radius: 8px;
    overflow: hidden;
  }

  & .ant-table-tbody {
    & > tr {
      & > td {
        padding: 4px 8px;
        border-bottom: 1px solid #f0f0f0;

        &:first-child {
          padding-left: 12px;
        }
        &:last-child {
          padding-right: 12px;
        }
      }
      &:hover {
        & > td {
          background: #f9fafb;
        }
      }
      &:last-child > td {
        border-bottom: none;
      }
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const TypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.33;
  color: ${({ type }: { type: string }) => {
        switch (type) {
            case 'sourcer': return '#059669';
            case 'active': return '#059669';
            case 'inactive': return '#dc2626';
            default: return '#4f46e5';
        }
    }};
  background-color: ${({ type }: { type: string }) => {
        switch (type) {
            case 'sourcer': return 'rgba(5, 150, 105, 0.1)';
            case 'active': return 'rgba(5, 150, 105, 0.1)';
            case 'inactive': return 'rgba(220, 38, 38, 0.1)';
            default: return 'rgba(79, 70, 229, 0.1)';
        }
    }};
  text-transform: capitalize;
  white-space: nowrap;
`;

const NoDataText = styled.div`
  padding: 32px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 8px;
  margin: 1rem 0 2rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UserDataWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StatNumber = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  margin-top: 2px;
  white-space: nowrap;
`;

const StatContainer = styled.div`
  padding: 2px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 70px;
`;

const StatsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`;

const RecruiterCompanyUsersSettings = () => {
    const [inviteForm] = Form.useForm();
    const { t } = useTranslation();
    const user = useAppSelector((state) => state.user.user);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [companyUsers, setCompanyUsers] = useState<RecruiterProfile[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [phone, setPhone] = useState<string>('');
    const theme = useHirePortTheme();
    const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);

    const fetchCompanyUsers = useCallback(async () => {
        if (user?.company?.id) {
            try {
                const response = await getCompanyRecruiters(user.company.id, page, pageSize);
                setCompanyUsers(response.data.results || []);
                setTotal(response.data.count || 0);
            } catch (error) {
                console.error('Error fetching company users', error);
                message.error(t('Failed to load company users'));
                // Optionally keep fallback or remove if not desired
                if (user && user.email) { // Simplified fallback check
                    setCompanyUsers([user as any]);
                    setTotal(1);
                }
            }
        }
    }, [page, pageSize, t, user]);

    // Fetch users on mount and when page/pageSize changes
    useEffect(() => {
        // Check conditions similar to menu visibility
        if (user?.company && user.recruiter_type !== 'freelance') {
            fetchCompanyUsers();
        }
    }, [user?.company, user?.recruiter_type, fetchCompanyUsers]); // Removed page/pageSize dependency here, handled by fetchCompanyUsers callback deps

    const handleInviteRecruiter = async (values: any) => {
        setInviteLoading(true);

        try {
            if (!user?.company?.id) {
                message.error(t('You need to be part of a company to invite users'));
                setInviteLoading(false);
                return;
            }

            const nameParts = values.name.trim().split(' ');
            const firstName = nameParts[0];
            const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
            const isSourcer = values.recruiter_type === 'sourcer';

            await createRecruiterProfile({
                email: values.email,
                first_name: firstName,
                last_name: lastName,
                city: values.city,
                phone: phone || undefined,
                is_sourcer: isSourcer
            });

            message.success(t('User was successfully added to your company'));
            inviteForm.resetFields();
            setPhone('');
            fetchCompanyUsers();
            setIsInviteModalVisible(false);
        } catch (error: any) {
            console.error('Error creating recruiter profile', error);
            const errorMessage = error.response?.data?.detail || t('Failed to add user to your company');
            message.error(errorMessage);
        } finally {
            setInviteLoading(false);
        }
    };

    const handleRemoveUser = async (recruiterId: string, recruiterName: string, isActive: boolean) => {
        Modal.confirm({
            title: isActive ? t('DEACTIVATE_USER') : t('REACTIVATE_USER'),
            content: isActive
                ? t('DEACTIVATE_USER_CONFIRM', { name: recruiterName })
                : t('REACTIVATE_USER_CONFIRM', { name: recruiterName }),
            okText: isActive ? t('Deactivate') : t('Reactivate'),
            okType: 'danger',
            cancelText: t('Cancel'),
            onOk: async () => {
                try {
                    await toggleRecruiterActiveStatus(recruiterId);
                    message.success(t('USER_STATUS_UPDATED'));
                    fetchCompanyUsers(); // Refresh list after status change
                } catch (error) {
                    console.error('Error toggling user status', error);
                    message.error(t('FAILED_UPDATE_USER_STATUS'));
                }
            }
        });
    };

    // Table columns definition (copied directly)
    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            width: 250,
            render: (text: string, record: RecruiterProfile) => {
                const firstName = record.first_name || '';
                const lastName = record.last_name || '';
                const fullName = `${firstName} ${lastName}`.trim();
                const displayTitle = fullName || record.email || '';
                const displayDescription = fullName ? record.email || '' : '';
                return (
                    <UserDataWrapper>
                        <NameImage
                            title={displayTitle}
                            image={record.image || ''}
                            description={displayDescription}
                            color={{
                                main: '#29ae5e',
                                light: '#eaf7ef'
                            }}
                        />
                    </UserDataWrapper>
                );
            },
        },
        {
            title: t('Type'),
            dataIndex: 'is_sourcer',
            key: 'is_sourcer',
            width: 100,
            render: (isSourcer: boolean, record: RecruiterProfile) => {
                const displayType = isSourcer ? 'sourcer' : 'standard';
                return (
                    <TypeBadge type={displayType}>
                        {displayType === 'sourcer' ? t('SOURCER') : t('STANDARD')}
                    </TypeBadge>
                );
            },
        },
        {
            title: t('Activity & Status'),
            key: 'activity_status',
            width: 300,
            render: (_: any, record: RecruiterProfile) => (
                <StatsWrapper>
                    <StatContainer>
                        <StatNumber>{record.introduced_count || 0}</StatNumber>
                        <StatLabel><UserAddOutlined /> {t('INTRODUCED')}</StatLabel>
                    </StatContainer>
                    <StatContainer>
                        <StatNumber>{record.in_process_count || 0}</StatNumber>
                        <StatLabel><TeamOutlined /> {t('IN_PROCESS')}</StatLabel>
                    </StatContainer>
                    <StatContainer>
                        <StatNumber>{record.hired_count || 0}</StatNumber>
                        <StatLabel><CheckCircleOutlined /> {t('HIRED')}</StatLabel>
                    </StatContainer>
                    {record.email !== user?.email && (
                        record.recruiter_type !== 'agency_admin' || record.is_sourcer) ? (
                        <Switch
                            size="small"
                            checked={record.is_active}
                            onChange={() => handleRemoveUser(record.id, record.name, record.is_active)}
                            checkedChildren={t('ACTIVE')}
                            unCheckedChildren={t('INACTIVE')}
                            style={{
                                backgroundColor: record.is_active ? theme.primaryColor : '#627d95',
                                opacity: record.is_active ? 1 : 0.7,
                                marginLeft: '16px'
                            }}
                        />
                    ) : (
                        <div style={{ width: '48px', marginLeft: '16px' }}></div>
                    )}
                </StatsWrapper>
            ),
        },
    ];

    const showInviteModal = () => {
        setIsInviteModalVisible(true);
    };

    const handleCancelInviteModal = () => {
        setIsInviteModalVisible(false);
        // Optionally reset form fields on cancel
        // inviteForm.resetFields();
        // setPhone('');
    };

    return (
        <PageWrapper>
            <FormWrapper>
                <SettingsMenu menu="recruiter-company-users" />

                <PageHeader>
                    <PageTitle>{t('COMPANY_SETTINGS')}</PageTitle>
                </PageHeader>

                {/* Users Table */}
                {companyUsers.length > 0 ? (
                    <>
                        <StyledTable
                            dataSource={companyUsers}
                            columns={columns as any} // Keep type assertion if necessary
                            rowKey="id"
                            pagination={false}
                            showHeader={false}
                        />
                        {total > pageSize && (
                            <PaginationWrapper>
                                <Pagination
                                    current={page}
                                    pageSize={pageSize}
                                    total={total}
                                    onChange={(newPage, newPageSize) => {
                                        setPage(newPage);
                                        if (newPageSize) setPageSize(newPageSize);
                                        fetchCompanyUsers();
                                    }}
                                />
                            </PaginationWrapper>
                        )}
                    </>
                ) : (
                    <NoDataText>
                        {t('NO_USERS_FOUND')}
                    </NoDataText>
                )}

                {/* Button to trigger the modal - Use custom Button */}
                <div style={{ marginTop: '2rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="primary" icon={<UserAddOutlined />} onClick={showInviteModal}>
                        {t('INVITE_NEW_USER')}
                    </Button>
                </div>

                {/* Invite User Modal */}
                <Modal
                    title={t('INVITE_NEW_USER')}
                    visible={isInviteModalVisible}
                    onCancel={handleCancelInviteModal}
                    footer={
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                            <Button key="back" onClick={handleCancelInviteModal}>
                                {t('Cancel')}
                            </Button>
                            <Button
                                key="submit"
                                type="primary"
                                loading={inviteLoading}
                                onClick={() => inviteForm.submit()}
                            >
                                {t('Add User')}
                            </Button>
                        </div>
                    }
                    width={600}
                >
                    <StyledForm
                        form={inviteForm}
                        layout="vertical"
                        name="invite-form"
                        onFinish={handleInviteRecruiter}
                        style={{ marginBottom: 0 }}
                    >
                        <FormRow>
                            <FormItem
                                label={t('NAME_LABEL')}
                                name="name"
                                rules={[{ required: true, message: t('REQUIRED_ERROR') }]}
                            >
                                <Input size="large" placeholder={t('Enter full name')} />
                            </FormItem>

                            <FormItem
                                label={t('EMAIL_LABEL')}
                                name="email"
                                rules={[{ required: true, message: t('REQUIRED_ERROR') }]}
                            >
                                <Input size="large" type="email" placeholder={t('Enter email address')} />
                            </FormItem>
                        </FormRow>

                        <FormRow>
                            <FormItem
                                label={t('PHONE_LABEL')}
                                name="phone"
                            >
                                <Input
                                    size="large"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder={t('Enter phone number (optional)')}
                                />
                            </FormItem>

                            <FormItem
                                label={t('CITY_LABEL')}
                                name="city"
                                rules={[{ required: true, message: t('REQUIRED_ERROR') }]}
                            >
                                <GooglePlacesInput
                                    size="large"
                                    placeholder={t('Enter city')}
                                    onChange={(cityName: string) => {
                                        if (cityName) {
                                            inviteForm.setFieldsValue({ city: cityName });
                                        }
                                    }}
                                />
                            </FormItem>
                        </FormRow>

                        <FormItem
                            label={t('USER_TYPE')}
                            name="recruiter_type"
                            initialValue="agency"
                        >
                            <RadioButtonTab
                                items={[
                                    {
                                        name: t('STANDARD'),
                                        value: 'agency',
                                    },
                                    {
                                        name: t('SOURCER'),
                                        value: 'sourcer',
                                    },
                                ]}
                            />
                        </FormItem>
                    </StyledForm>
                </Modal>

            </FormWrapper>
        </PageWrapper>
    );
};

export default RecruiterCompanyUsersSettings; 